import {
  REQUEST_DATE_FORMAT,
  US_MEDICAL_DATE_FORMAT,
  US_MEDICAL_DATE_TIME_FORMAT,
  US_SHORT_DATE_FORMAT,
} from 'const/dateTime';
import moment, { parseZone } from 'moment/moment';

const DATE_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'DD.MM.YYYY',
  'DD-MM-YYYY',
  'YYYY/MM/DD',
  'MM-DD-YYYY',
  'DD/MM/YYYY',
  'YYYY.MM.DD',
  'MM.DD.YYYY',
];

/**
 * Converts a date to a string in the format expected by the backend.
 * Also used for the FormDateTimePickerAdapter component by default.
 * Uses US_DATE_TIME_FORMAT for the format.
 * @param {Date | moment.Moment | string} date - The date to format without timezone.
 * @returns {string} The formatted date string.
 */
export const defaultDateTimeFormat = (
  date: Date | moment.Moment | string,
): string => parseZone(date).format(US_MEDICAL_DATE_TIME_FORMAT);

/**
 * Converts a date to a string in the format expected by the backend.
 * Also used for the FormDatePickerAdapter component by default.
 * Uses US_DATE_FORMAT for the format.
 * @param {Date | moment.Moment | string} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const defaultDateFormat = (
  date: Date | moment.Moment | string,
): string => {
  if (typeof date === 'string') {
    for (const format of DATE_FORMATS) {
      const momentDate = moment(date, format, true);
      if (momentDate.isValid()) {
        return momentDate.format(US_MEDICAL_DATE_FORMAT);
      }
    }

    const fallbackDate = moment(date);
    return fallbackDate.isValid()
      ? fallbackDate.format(US_MEDICAL_DATE_FORMAT)
      : 'Invalid date';
  }

  return moment(date).format(US_MEDICAL_DATE_FORMAT);
};

export const responseToLocalDateString = (
  date: string,
  format = US_MEDICAL_DATE_TIME_FORMAT,
  dateOnly: boolean = false,
): string => {
  for (const dateFormat of DATE_FORMATS) {
    const momentDate = dateOnly
      ? moment.utc(date, dateFormat, true)
      : moment(date, dateFormat, true);
    if (momentDate.isValid()) {
      return momentDate.format(format);
    }
  }

  if (dateOnly) {
    const fallbackDate = moment.utc(date);
    return fallbackDate.isValid()
      ? fallbackDate.format(format)
      : 'Invalid date';
  }

  return moment.utc(date).local().format(format);
};

/**
 * Formats a date for API requests based on whether time should be included.
 * @param {Date | moment.Moment | string} date - The date to format
 * @param {boolean} [withTime=true] - Whether to include time in the formatted date
 * @returns {string} ISO string with time (if withTime=true) or formatted date string (if withTime=false)
 */
export const dateToRequest = (
  date: Date | moment.Moment | string,
  withTime: boolean = true,
): string => {
  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return 'Invalid date';
  }

  if (withTime) {
    return momentDate.utc().toISOString();
  } else {
    return momentDate.format(REQUEST_DATE_FORMAT);
  }
};

/**
 * Converts a date-only value to a UTC ISO string while assigning a default time
 * in the user's local timezone.
 * If the date is today, uses the current time. Otherwise, uses the provided default time.
 * @param {Date | moment.Moment | string} date - The date to convert
 * @param {string} [inputFormat] - Optional format used to parse string input
 * @param {number} [defaultHour=12] - Default hour to set when time is missing (for past/future dates)
 * @param {number} [defaultMinute=0] - Default minute to set when time is missing (for past/future dates)
 * @returns {string} ISO string in UTC with the default time applied when needed
 */
export const dateOnlyToUtcWithDefaultTime = (
  date: Date | moment.Moment | string,
  inputFormat?: string,
  defaultHour: number = 12,
  defaultMinute: number = 0,
): string => {
  const parsedDate =
    typeof date === 'string' && inputFormat
      ? moment(date, inputFormat, true)
      : moment(date);

  if (!parsedDate.isValid()) {
    return 'Invalid date';
  }

  // If the date is today, use current time; otherwise use provided defaults
  const now = moment();
  const hour = isToday(parsedDate) ? now.hour() : defaultHour;
  const minute = isToday(parsedDate) ? now.minute() : defaultMinute;

  const dateWithTime = parsedDate.clone().set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  });

  return dateWithTime.utc().toISOString();
};

export const isToday = (date: Date | moment.Moment | string): boolean => {
  return moment(date).isSame(new Date(), 'day');
};

/**
 * Strips timezone and time information from a date string, returning only the date part.
 * This handles various ISO 8601 formats and other common date-time representations.
 * @param {string} dateString - The date string from API (various formats supported)
 * @returns {string} Date string in YYYY-MM-DD format, or original if invalid
 */
export const stripTimezoneFromDate = (dateString: string): string => {
  if (!dateString || typeof dateString !== 'string') return dateString;

  // Handle various ISO 8601 and common formats:
  // - "2024-03-15T00:00:00Z"
  // - "2024-03-15T00:00:00+00:00"
  // - "2024-03-15 00:00:00"
  // - "2024-03-15T00:00:00.000Z"
  // - Already stripped: "2024-03-15"

  const datePart = dateString.split(/[T ]/)[0];

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  return dateString;
};

/**
 * US short calendar date (MM/DD/YY) for API values that are date-only or ISO with a fixed calendar day
 * (same idea as {@link stripTimezoneFromDate} / {@link responseToDateOnlyString}: no shifting the day
 * when the payload is a plain date).
 */
export function formatDateOnlyShortUs(value: string): string {
  if (!value) {
    return value;
  }

  const datePart = stripTimezoneFromDate(value);
  const strict = moment(datePart, REQUEST_DATE_FORMAT, true);
  if (strict.isValid()) {
    return strict.format(US_SHORT_DATE_FORMAT);
  }

  const fallback = moment(value);
  return fallback.isValid() ? fallback.format(US_SHORT_DATE_FORMAT) : value;
}

/**
 * Converts a date-only string from API response, stripping timezone information.
 * This function is specifically for date-only fields (DOB, date of diagnosis, etc.) that should
 * not be affected by timezone conversions.
 * @param {string} date - The date string (expected format: YYYY-MM-DD or ISO with timezone)
 * @param {string} [format=REQUEST_DATE_FORMAT] - The output format (default: YYYY-MM-DD for storage)
 * @returns {string} The formatted date string without timezone conversion
 */
export const responseToDateOnlyString = (
  date: string,
  format = REQUEST_DATE_FORMAT,
): string => {
  if (!date) return date;

  const dateOnlyString = stripTimezoneFromDate(date);
  return responseToLocalDateString(dateOnlyString, format, true);
};

/**
 * Formats a date-only field for API requests without timezone conversion.
 * This function is specifically for date-only fields that should be sent as date strings.
 * @param {Date | moment.Moment | string} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const dateOnlyToRequest = (
  date: Date | moment.Moment | string,
): string => {
  if (typeof date === 'string') {
    for (const format of DATE_FORMATS) {
      const momentDate = moment(date, format, true);
      if (momentDate.isValid()) {
        return momentDate.format(REQUEST_DATE_FORMAT);
      }
    }

    const fallbackDate = moment(date);
    return fallbackDate.isValid()
      ? fallbackDate.format(REQUEST_DATE_FORMAT)
      : 'Invalid date';
  }

  const momentDate = moment(date);
  return momentDate.isValid()
    ? momentDate.utc().format(REQUEST_DATE_FORMAT)
    : 'Invalid date';
};
